// @ts-check

/**
 * @file Provides a shared modal action menu.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import Text from "@kaspernj/api-maker/build/utils/text"
import {memo} from "react"
import {Pressable, View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"

import Link from "./link"
import Modal from "./modal"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {BoardThemeColors} from "../shared/theme" */
/** @typedef {import("react").ComponentProps<typeof FontAwesome6>["name"]} FontAwesome6Name */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} ActionMenuItem
 * @property {boolean} [destructive] Whether the action is destructive.
 * @property {boolean} [disabled] Whether the action is disabled.
 * @property {string} [href] Optional route path for navigation actions.
 * @property {FontAwesome6Name} [iconName] Optional icon shown left of the label.
 * @property {string} label Action label.
 * @property {() => unknown} [onPress] Optional press handler run before navigation or for press actions.
 * @property {string} testID Stable test id.
 */

/**
 * @typedef {object} ActionMenuProps
 * @property {boolean} isVisible Whether the menu is visible.
 * @property {ActionMenuItem[]} items Menu actions.
 * @property {() => unknown} onClosePress Close-button press handler.
 * @property {string} [testID] Stable test id.
 * @property {string} title Menu title.
 */

/** @typedef {Record<string, never>} ActionMenuState */

/**
 * Shared modal action menu.
 * @augments {ShapeComponent<ActionMenuProps, ActionMenuState>}
 */
class ActionMenu extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  static propTypes = PropTypesExact({
    isVisible: PropTypes.bool.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        destructive: PropTypes.bool,
        disabled: PropTypes.bool,
        href: PropTypes.string,
        iconName: PropTypes.string,
        label: PropTypes.string.isRequired,
        onPress: PropTypes.func,
        testID: PropTypes.string.isRequired
      }).isRequired
    ).isRequired,
    onClosePress: PropTypes.func.isRequired,
    testID: PropTypes.string,
    title: PropTypes.string.isRequired
  })

  static defaultProps = {
    testID: undefined
  }

  /** Initializes locale hooks for translated labels. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the modal action menu.
   * @returns {ReactNode} Action menu content.
   */
  render() {
    const testID = this.p.testID
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    return (
      <Modal
        cardStyle={styles.card ||= {
          maxWidth: 360,
          padding: 20
        }}
        isVisible={this.p.isVisible}
        onRequestClose={this.p.onClosePress}
        testID={testID}
      >
        <View
          style={styles.header ||= {
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: 16
          }}
          testID={testID ? `${testID}/header` : undefined}
        >
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.text,
              flex: 1,
              fontSize: 18,
              fontWeight: 700,
              paddingRight: 12
            }}
            testID={testID ? `${testID}/title` : undefined}
          >
            {this.p.title}
          </Text>
          <Pressable
            accessibilityLabel={_("Close")}
            onPress={this.p.onClosePress}
            style={styles[`closeButton-${themeName}`] ||= {
              alignItems: "center",
              backgroundColor: colors.surfaceMuted,
              borderRadius: 999,
              justifyContent: "center",
              minHeight: 36,
              minWidth: 36
            }}
            testID={testID ? `${testID}/closeButton` : undefined}
          >
            <View
              style={styles.closeButtonIcon ||= {
                alignItems: "center",
                justifyContent: "center"
              }}
              testID={testID ? `${testID}/closeButton/icon` : undefined}
            >
              <FontAwesome6 color={colors.text} name="xmark" size={14} />
            </View>
          </Pressable>
        </View>
        <View
          style={styles.items ||= {
            gap: 10
          }}
          testID={testID ? `${testID}/items` : undefined}
        >
          {this.p.items.map((item) => renderActionMenuItem(item, colors, themeName))}
        </View>
      </Modal>
    )
  }
}

/** @type {NamedExoticComponent<ActionMenuProps>} */
const ActionMenuComponent = memo(shapeComponent(ActionMenu))

export default ActionMenuComponent

/**
 * Renders one action menu item.
 * @param {ActionMenuItem} item Menu item to render.
 * @param {BoardThemeColors} colors Active theme colors.
 * @param {string} themeName Active theme name.
 * @returns {ReactNode} Pressable or link-backed row.
 * @throws {Error} When an enabled item has neither href nor onPress.
 */
function renderActionMenuItem(item, colors, themeName) {
  const destructive = Boolean(item.destructive)
  const disabled = Boolean(item.disabled)
  const itemStyle = styles[`item-${themeName}-${destructive}-${disabled}`] ||= {
    alignItems: "center",
    backgroundColor: disabled
      ? colors.disabledBackground
      : destructive ? colors.dangerBackground : colors.infoBackground,
    borderColor: disabled
      ? colors.disabledBackground
      : destructive ? colors.danger : colors.info,
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: "row",
    opacity: disabled ? 0.75 : 1,
    paddingHorizontal: 14,
    paddingVertical: 12
  }
  const content = renderActionMenuItemContent(item, colors, themeName)

  if (item.href && !disabled) {
    return (
      <Link asChild href={item.href} key={item.testID}>
        <Pressable
          onPress={item.onPress}
          style={itemStyle}
          testID={item.testID}
        >
          {content}
        </Pressable>
      </Link>
    )
  }

  if (!item.onPress && !disabled) {
    throw new Error(`Action menu item ${item.testID} must have href or onPress`)
  }

  return (
    <Pressable
      disabled={disabled}
      key={item.testID}
      onPress={item.onPress}
      style={itemStyle}
      testID={item.testID}
    >
      {content}
    </Pressable>
  )
}

/**
 * Renders the item icon and label.
 * @param {ActionMenuItem} item Menu item to render.
 * @param {BoardThemeColors} colors Active theme colors.
 * @param {string} themeName Active theme name.
 * @returns {ReactNode} Icon and label content.
 */
function renderActionMenuItemContent(item, colors, themeName) {
  const destructive = Boolean(item.destructive)
  const disabled = Boolean(item.disabled)
  const textColor = disabled
    ? colors.disabledText
    : destructive ? colors.dangerText : colors.link

  return (
    <>
      {item.iconName &&
        <View
          style={styles.itemIcon ||= {
            alignItems: "center",
            justifyContent: "center",
            marginRight: 10,
            width: 16
          }}
          testID={`${item.testID}/icon`}
        >
          <FontAwesome6
            color={textColor}
            name={item.iconName}
            size={14}
          />
        </View>
      }
      <Text
        style={styles[`itemText-${themeName}-${destructive}-${disabled}`] ||= {
          color: textColor,
          flex: 1,
          fontSize: 15,
          fontWeight: 600
        }}
        testID={`${item.testID}/text`}
      >
        {item.label}
      </Text>
    </>
  )
}
